import { createFileRoute, Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { queryOptions, useSuspenseQuery } from "@tanstack/react-query";
import { listPublishedArticles } from "@/lib/articles.functions";

const articlesQuery = queryOptions({
  queryKey: ["articles", "published"],
  queryFn: () => listPublishedArticles(),
});

export const Route = createFileRoute("/blog")({
  head: () => ({
    meta: [
      { title: "Morocco Travel Guides & Airport Transfer Tips | Bacha Way" },
      {
        name: "description",
        content:
          "Practical guides, city itineraries and insider tips to make your trip to Morocco effortless — from arrival to the last mile.",
      },
      { property: "og:title", content: "Morocco Travel Guides & Airport Transfer Tips" },
      {
        property: "og:description",
        content: "Practical guides, city itineraries and insider tips to make your trip to Morocco effortless.",
      },
      { property: "og:url", content: "https://bachawaytravel.com/blog" },
    ],
    links: [{ rel: "canonical", href: "https://bachawaytravel.com/blog" }],
  }),
  loader: ({ context }) => context.queryClient.ensureQueryData(articlesQuery),
  errorComponent: ({ error }) => (
    <div className="mx-auto max-w-3xl px-6 py-24 text-center">
      <h1 className="font-display text-2xl text-foreground">Couldn't load articles</h1>
      <p className="mt-2 text-sm text-muted-foreground">{error.message}</p>
    </div>
  ),
  component: BlogPage,
});

function BlogPage() {
  const { t } = useTranslation();
  const { data } = useSuspenseQuery(articlesQuery);
  const posts = data.articles;
  const categories = Array.from(new Set(posts.map((p) => p.category).filter(Boolean))) as string[];

  return (
    <>
      <section className="bg-hero-navy text-white">
        <div className="mx-auto max-w-7xl px-6 py-20 md:py-24 text-center">
          <h1 className="mx-auto max-w-3xl text-4xl md:text-6xl font-bold leading-[1.05]">
            {t("blog.title1")} <span className="text-gradient-sky">{t("blog.title2")}</span>
          </h1>
          <p className="mt-5 mx-auto max-w-2xl text-white/70">{t("blog.lead")}</p>
        </div>
      </section>

      {categories.length > 0 && (
        <section className="py-8 bg-background border-b border-border">
          <div className="mx-auto max-w-7xl px-6 flex flex-wrap gap-2">
            <span className="rounded-full px-4 py-2 text-sm font-medium bg-primary text-primary-foreground">
              All
            </span>
            {categories.map((c) => (
              <span
                key={c}
                className="rounded-full px-4 py-2 text-sm font-medium bg-secondary text-secondary-foreground"
              >
                {c}
              </span>
            ))}
          </div>
        </section>
      )}

      <section className="py-16 bg-background">
        <div className="mx-auto max-w-7xl px-6">
          {posts.length === 0 ? (
            <div className="text-center py-16 text-muted-foreground">
              No articles published yet. Check back soon.
            </div>
          ) : (
            <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
              {posts.map((p) => (
                <Link
                  key={p.id}
                  to="/blog/$slug"
                  params={{ slug: p.slug }}
                  className="group rounded-2xl border border-border bg-card overflow-hidden hover:border-primary/40 hover:shadow-xl hover:shadow-primary/5 transition-all flex flex-col"
                >
                  <div className="aspect-[16/10] bg-hero-navy overflow-hidden">
                    {p.cover_image_url ? (
                      <img
                        src={p.cover_image_url}
                        alt={p.title}
                        loading="lazy"
                        className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    ) : null}
                  </div>
                  <div className="p-6 flex-1 flex flex-col">
                    {p.category && (
                      <div className="text-xs uppercase tracking-[0.18em] text-primary font-semibold">
                        {p.category}
                      </div>
                    )}
                    <h2 className="mt-3 text-xl font-bold leading-snug group-hover:text-primary transition-colors">
                      {p.title}
                    </h2>
                    {p.excerpt && (
                      <p className="mt-3 text-sm text-muted-foreground line-clamp-3">{p.excerpt}</p>
                    )}
                    <div className="mt-5 flex items-center justify-between text-xs text-muted-foreground">
                      <span>{p.author || "Bacha Way"}</span>
                      {p.published_at && (
                        <span>{new Date(p.published_at).toLocaleDateString()}</span>
                      )}
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
        <div className="mx-auto max-w-7xl px-6 mt-12 text-center">
          <Link
            to="/contact"
            className="inline-flex rounded-full border border-border bg-secondary px-6 py-3 text-sm font-semibold text-foreground hover:bg-primary hover:text-primary-foreground transition-colors"
          >
            {t("blog.suggest")}
          </Link>
        </div>
      </section>
    </>
  );
}
